import { Injectable } from "@angular/core";

import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { v4 as uuid } from "uuid";

import { EjendomsfaktorDefinition, EjendomsfaktorDefinitionDto } from "@e-forsyning/common/ejendomsfaktor";

import { BatteriskiftMapper } from "../opgave/arbejdsgang/batteriskift/batteriskift.mapper";
import { BatteriskiftData } from "../opgave/arbejdsgang/batteriskift/model/batteriskift-data.model";
import { ServicerapportEjendomsfaktorMapper } from "../opgave/arbejdsgang/ejendomsfaktorer/ejendomsfaktor.mapper";
import { EjendomsfaktorerData } from "../opgave/arbejdsgang/ejendomsfaktorer/model/ejendomsfaktorer-data.model";
import { InstallationsoprettelseMapper } from "../opgave/arbejdsgang/installationsoprettelse/installationsoprettelse.mapper";
import { InstallationsoprettelseData } from "../opgave/arbejdsgang/installationsoprettelse/model/installationsoprettelse-data.model";
import { KomponentMapper } from "../opgave/arbejdsgang/komponenter/komponent.mapper";
import { KomponentGruppe } from "../opgave/arbejdsgang/komponenter/model/komponent-gruppe.model";
import { MaalerskiftMapper } from "../opgave/arbejdsgang/maalerskift/maalerskift.mapper";
import { MaalerskiftData } from "../opgave/arbejdsgang/maalerskift/model/maalerskift-data.model";
import { OpgaveNoterData } from "../opgave/arbejdsgang/noter/model/opgave-noter-data.model";
import { OpgaveNoteMapper } from "../opgave/arbejdsgang/noter/opgave-note.mapper";
import { SendMailTilForbrugerData } from "../opgave/arbejdsgang/send-mail-til-forbruger/model/send-mail-til-forbruger-data.model";
import { SendMailTilForbrugerMapper } from "../opgave/arbejdsgang/send-mail-til-forbruger/send-mail-til-forbruger.mapper";
import { Opgave } from "../opgave/model/opgave.model";

@Injectable({ providedIn: "root" })
export class OpgaveMapper {
    constructor(
        private readonly ejendomsfaktorMapper: ServicerapportEjendomsfaktorMapper,
        private readonly installationsoprettelseMapper: InstallationsoprettelseMapper,
        private readonly komponentMapper: KomponentMapper,
        private readonly maalerskiftMapper: MaalerskiftMapper,
        private readonly batteriskiftMapper: BatteriskiftMapper,
        private readonly opgaveNoteMapper: OpgaveNoteMapper,
        private readonly sendMailTilForbrugerMapper: SendMailTilForbrugerMapper
    ) {}

    public dtoToOpgave(data: any): Observable<Opgave> {
        const result = this.opgaveNoteMapper
            .getBilagUrlTemplate()
            .pipe(map((bilagUrlTemplate: string) => this.dtoToOpgaveWithBilagUrlTemplate(data, bilagUrlTemplate)));
        return result;
    }

    public opgaveToDto(opgave: Opgave): any {
        let result: any = {
            ...opgave,
            arbejdsgange: opgave.arbejdsgange.map((arbejdsgang) => ({
                ...arbejdsgang,
                data:
                    arbejdsgang.type === "komponentgruppe"
                        ? this.komponentMapper.komponentGruppeToDto(arbejdsgang.data as KomponentGruppe)
                        : arbejdsgang.type === "noter"
                        ? this.opgaveNoteMapper.opgaveNoterDataToDto(arbejdsgang.data as OpgaveNoterData)
                        : arbejdsgang.type === "ejendomsfaktorer"
                        ? this.ejendomsfaktorMapper.ejendomsfaktorerDataToDto(arbejdsgang.data as EjendomsfaktorerData)
                        : arbejdsgang.type === "maalerskift"
                        ? this.maalerskiftMapper.maalerskiftDataToDto(arbejdsgang.data as MaalerskiftData)
                        : arbejdsgang.type === "batteriskift"
                        ? this.batteriskiftMapper.batteriskiftDataToDto(arbejdsgang.data as BatteriskiftData)
                        : arbejdsgang.type === "installationsoprettelse"
                        ? this.installationsoprettelseMapper.installationsoprettelsDataToDto(
                              arbejdsgang.data as InstallationsoprettelseData
                          )
                        : arbejdsgang.type === "send-mail-til-forbruger"
                        ? this.sendMailTilForbrugerMapper.sendMailTilForbrugerDataToDto(arbejdsgang.data as SendMailTilForbrugerData)
                        : null
            }))
        };
        result = this.ejendomsfaktorMapper.opdelEjendomsfaktorer(result);
        return result;
    }

    public dtoToEjendomsfaktorDefinitioner(dto: EjendomsfaktorDefinitionDto[]): EjendomsfaktorDefinition[] {
        return this.ejendomsfaktorMapper.dtoToEjendomsfaktorDefinitioner(dto);
    }

    private dtoToOpgaveWithBilagUrlTemplate(data: any, bilagUrlTemplate: string): Opgave {
        data = this.ejendomsfaktorMapper.grupperEjendomsfaktorer(data);
        const result: any = {
            ...data,
            uid: data.uid || uuid(),
            arbejdsgange: data.arbejdsgange.map((arbejdsgang: any) => ({
                ...arbejdsgang,
                data:
                    arbejdsgang.type === "komponentgruppe"
                        ? this.komponentMapper.dtoToKomponentGruppe(arbejdsgang.data, bilagUrlTemplate)
                        : arbejdsgang.type === "noter"
                        ? this.opgaveNoteMapper.dtoToOpgaveNoterData(arbejdsgang.data, bilagUrlTemplate)
                        : arbejdsgang.type === "ejendomsfaktorer"
                        ? this.ejendomsfaktorMapper.dtoToEjendomsfaktorerData(arbejdsgang.data)
                        : arbejdsgang.type === "maalerskift"
                        ? this.maalerskiftMapper.dtoToMaalerskiftData(arbejdsgang.data, bilagUrlTemplate)
                        : arbejdsgang.type === "batteriskift"
                        ? this.batteriskiftMapper.dtoToBatteriskiftData(arbejdsgang.data, bilagUrlTemplate)
                        : arbejdsgang.type === "installationsoprettelse"
                        ? this.installationsoprettelseMapper.dtoToInstallationsoprettelseData(arbejdsgang.data, bilagUrlTemplate)
                        : arbejdsgang.type === "send-mail-til-forbruger"
                        ? this.sendMailTilForbrugerMapper.dtoToSendMailTilForbrugerData(arbejdsgang.data)
                        : null
            }))
        };
        return result as Opgave;
    }
}
